<template>
  <g class="render-svg-photographed-frame">
    <defs v-if="clip || region">
      <clipPath v-if="clip" :id="clipId">
        <ellipse :cx="clip.cx" :cy="clip.cy" :rx="clip.rx" :ry="clip.ry" />
      </clipPath>
      <clipPath v-if="region" :id="regionId">
        <rect :x="region.x" :y="region.y" :width="region.width" :height="region.height" />
      </clipPath>
      <clipPath v-if="region" :id="artworkAreaId">
        <rect :x="opening.x" :y="opening.y" :width="opening.width" :height="opening.height" />
      </clipPath>
    </defs>

    <g :clip-path="clip ? `url(#${clipId})` : null">
      <RenderSvgArtwork
        class="render-svg-photographed-frame__artwork"
        :frameSpec="frameSpec"
        :artworkKey="0"
        :x="opening.x"
        :y="opening.y"
        :openingWidthInInches="opening.width"
        :openingHeightInInches="opening.height"
        :allowUpload="allowUpload"
        :loading="loading"
        :readonly="readonly"
        @upload-click="$emit('upload-click')"
      />
    </g>

    <image
      class="render-svg-photographed-frame__mask"
      :class="{ 'render-svg-photographed-frame__mask--set': region }"
      :clip-path="region ? `url(#${regionId})` : null"
      :href="maskedRendering.imageUrl"
      :x="mask.x"
      :y="mask.y"
      :width="mask.size"
      :height="mask.size"
      preserveAspectRatio="none"
    />

    <!-- The same photo again over the artwork, drawn normally, so the frame covers the art -->
    <image
      v-if="region"
      class="render-svg-photographed-frame__mask"
      :clip-path="`url(#${artworkAreaId})`"
      :href="maskedRendering.imageUrl"
      :x="mask.x"
      :y="mask.y"
      :width="mask.size"
      :height="mask.size"
      preserveAspectRatio="none"
    />
  </g>
</template>

<script>
/**
 * Draws a frame that was photographed instead of drawn.
 *
 * The photograph is a square with the frame somewhere inside it and a hole where the
 * artwork shows through. Everything here is the arithmetic that puts that square on a wall
 * measured in inches: the frame's own box inside the photo is stretched to the space the
 * wall reserved for it, and the rest of the photo follows at the same scale, so the frame
 * lands exactly where the layout says and the hole lands wherever the photographer put it.
 *
 * The mask goes on top because it is opaque everywhere except that hole, which is what
 * lets the artwork run past the edges of the opening without showing.
 *
 * A photo of a whole set is shot on white rather than cut out, so a frame drawn from one
 * draws only its own slice and multiplies it onto what is behind, which leaves the white
 * backdrop invisible on any wall while the frame and its shadow still show. Multiplying
 * would also let the artwork show through the frame wherever the two overlap, so over the
 * artwork the photo is drawn a second time as it is. There is no white backdrop inside the
 * opening's box to hide, only frame and hole.
 *
 * No moulding is passed to the artwork. The artwork insets itself by a drawn moulding's
 * rabbet, but the photographed hole is already the visible opening, so the inset would be
 * counted twice and leave a band of bare backing inside the frame.
 */
import uuidv4 from "uuid/v4";
import RenderSvgArtwork from './RenderSvgArtwork.vue';
import { photographedFrameGeometry } from '../../photographedFrameGeometry';

export default {
  components: {
    RenderSvgArtwork
  },
  props: {
    frameSpec: {
      type: Object,
      required: true
    },
    maskedRendering: {
      type: Object,
      required: true
    },
    widthInInches: {
      type: Number,
      required: true
    },
    allowUpload: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },
  // Clicks and drops are left to the frame's own root element, which already handles both
  // and is the only opening there is to hit on a photographed frame.
  emits: ['upload-click'],
  data() {
    return {
      uuid: uuidv4()
    };
  },
  computed: {
    geometry() {
      return photographedFrameGeometry({
        maskedRendering: this.maskedRendering,
        widthInInches: this.widthInInches
      });
    },
    mask() {
      return this.geometry.mask;
    },
    opening() {
      return this.geometry.opening;
    },
    clip() {
      return this.geometry.clip;
    },
    clipId() {
      return `${this.uuid}-opening`;
    },
    region() {
      return this.geometry.region;
    },
    regionId() {
      return `${this.uuid}-region`;
    },
    artworkAreaId() {
      return `${this.uuid}-artwork-area`;
    }
  }
};
</script>

<style lang="scss" scoped>
// .render-svg-photographed-frame
.render-svg-photographed-frame {
  // .render-svg-photographed-frame__mask
  &__mask {
    pointer-events: none;

    // .render-svg-photographed-frame__mask--set
    &--set {
      mix-blend-mode: multiply;
    }
  }
}
</style>
